import { GridFiltersController } from "@repo/baseCloud";
import { FilterDropdown, FiltersV2, Loading, TextBox } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import type { LocationsFilter } from "../../models/locations";

import LocationsApi from "../../api/locationsApi";
import { QUERY_KEYS } from "../../api/queries";
import { useVerbiageQuery } from "../../api/verbiageApi";
import { CompanySegmentFilters } from "../../components/segmentFilters/companySegment";
import { useUserContext } from "../../contexts/userContext";
import { LocationsGrid } from "./grid";
import {
	ADDRESS,
	BRANCH,
	BRAND,
	CHANNEL,
	CHANNEL_BRANCH,
	CITY,
	MARKETER_BRANCH,
	NAME,
	PARTNER,
	PARTNER_TYPE,
	STATE,
	STATE_BRANCH,
	STATUS,
	ZIP,
} from "./strings";

const initialFilters: LocationsFilter = {
	currentPage: 1,
	itemsPerPage: 10,

	brand: null,
	channel: null,
	partnerType: null,
	partner: null,
	name: "",
	status: null,

	address: "",
	city: "",
	state: null,
	zip: "",

	channel_branch: null,
	marketer_branch: null,
	state_branch: null,
	branch: null,
};

const COMBO_STATUS_OPTIONS = [
	{ value: "Inactive", id: "1" },
	{ value: "Corporate Approved", id: "2" },
	{ value: "Pending RoE", id: "3" },
	{ value: "RoE Granted", id: "4" },
];

export function LocationsPage() {
	const [filters, setFilters] = useState<LocationsFilter>(initialFilters);
	const [appliedFilters, setAppliedFilters] = useState<LocationsFilter>(initialFilters);

	const { verbiage, verbiageLoading } = useVerbiageQuery();
	const { user, loading: userLoading } = useUserContext();
	const isVendor = user?.userIsVendorUser || user?.userIsVendorAdmin;

	// Dropdown queries (top section)
	const brandsQuery = useQuery({ queryKey: [QUERY_KEYS.Locations.Brands], queryFn: () => LocationsApi.getBrands() });
	const channelsQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.Channels],
		queryFn: () => LocationsApi.getChannels(),
	});
	const partnerTypesQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.PartnerTypes],
		queryFn: () => LocationsApi.getPartnerTypes(),
	});
	const partnersQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.Partners, filters.brand, filters.channel, filters.state, filters.partnerType],
		queryFn: () =>
			LocationsApi.getPartners({
				brandId: filters.brand,
				channelId: filters.channel,
				state: filters.state,
				partnerTypeId: filters.partnerType,
			}),
	});

	// Address states (depends on top filters)
	const statesQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.LocationStates, filters.brand, filters.channel, filters.partnerType],
		queryFn: () =>
			LocationsApi.getLocationStates({
				brandId: filters.brand,
				channelId: filters.channel,
				state: filters.state,
				partnerTypeId: filters.partnerType,
			}),
	});

	const statusesQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.Statuses],
		queryFn: () => GridFiltersController.getLocationStatuses(),
	});

	// Branch filters
	const branchChannelsQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.BranchChannels],
		queryFn: () => LocationsApi.getBranchChannels(),
	});

	const branchMarketersQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.BranchMarketers, filters.channel_branch, filters.state_branch, filters.branch],
		queryFn: async () => {
			const marketers = await LocationsApi.getMarketers({
				channelId: filters.channel_branch,
				state: filters.state_branch,
				branchId: filters.branch,
			});

			return [{ Id: 0, Name: "Unassigned" }, ...marketers];
		},
	});

	const branchStatesQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.BranchStates, filters.channel_branch, filters.marketer_branch, filters.branch],
		queryFn: () =>
			LocationsApi.getStates({
				channelId: filters.channel_branch,
				marketer: filters.marketer_branch,
				branchId: filters.branch,
			}),
	});

	const branchesQuery = useQuery({
		queryKey: [
			QUERY_KEYS.Locations.Branches,
			filters.channel_branch,
			filters.marketer_branch,
			filters.state_branch,
		],
		queryFn: async () => {
			const branches = await LocationsApi.getBranches({
				channelId: filters.channel_branch,
				marketer: filters.marketer_branch,
				state: filters.state_branch,
			});
			return [{ BranchId: 0, Name: "Unassigned" }, ...branches];
		},
	});

	const usersQuery = useQuery({
		queryKey: [QUERY_KEYS.Users],
		queryFn: () => GridFiltersController.getUsers(true),
	});

	const filterChanged = (value: any, name: keyof LocationsFilter) => {
		setFilters((prev) => ({ ...prev, [name]: value }));
	};

	const companySegmentFiltersChanged = useCallback(
		(s) =>
			setFilters((prev) => ({
				...prev,
				companies: s.companyIds,
				divisions: s.companyDivisionIds,
				regions: s.companyRegionIds,
				markets: s.companyMarketIds,
				companyManagementAreaIds: s.companyManagementAreaIds,
			})),
		[setFilters],
	);

	return (
		<Loading loading={verbiageLoading || userLoading} fullHeight fullWidth>
			<main>
				<FiltersV2
					onApply={() =>
						setAppliedFilters((currentFilters) => ({
							...filters,
							currentPage: 1,
							itemsPerPage: currentFilters.itemsPerPage,
						}))
					}
				>
					{/* Location Properties */}
					{!isVendor && <CompanySegmentFilters onChange={companySegmentFiltersChanged} />}
					<FilterDropdown
						id={"assignedUser"}
						label={"Assigned AE"}
						objectName={`Assigned Users`}
						values={usersQuery.data ?? []}
						value={filters.assignedUser}
						changeHandler={filterChanged}
						clearable
						loaded={!usersQuery.isLoading}
						fullLength
						valueProp="id"
						textProp="value"
					/>
					<FilterDropdown
						id={BRAND}
						objectName="Brands"
						label="Brand"
						values={brandsQuery.data ?? []}
						value={filters.brand}
						changeHandler={filterChanged}
						clearable
						loaded={!brandsQuery.isLoading}
						fullLength
						isMulti
						valueProp="Id"
						textProp="Name"
					/>
					<FilterDropdown
						id={CHANNEL}
						objectName="Channels"
						label="Channel"
						values={channelsQuery.data ?? []}
						value={filters.channel}
						changeHandler={filterChanged}
						clearable
						loaded={!channelsQuery.isLoading}
						fullLength
						isMulti
						valueProp="Id"
						textProp="Name"
						hidden
					/>
					<FilterDropdown
						id={PARTNER_TYPE}
						objectName={`${verbiage.partnerType}s`}
						label={verbiage.partnerType}
						values={partnerTypesQuery.data ?? []}
						value={filters.partnerType}
						changeHandler={filterChanged}
						clearable
						loaded={!partnerTypesQuery.isLoading}
						fullLength
						isMulti
						valueProp="id"
						textProp="value"
					/>
					<FilterDropdown
						id={PARTNER}
						objectName={`${verbiage.partner}s`}
						label={verbiage.partner}
						values={partnersQuery.data ?? []}
						value={filters.partner}
						changeHandler={filterChanged}
						clearable
						loaded={!partnersQuery.isLoading}
						fullLength
						isMulti
						valueProp="id"
						textProp="value"
					/>
					<TextBox
						id={NAME}
						label="Name"
						placeholder="Contains..."
						value={filters.name ?? ""}
						changeHandler={filterChanged}
					/>
					<TextBox
						id={"code"}
						label="Code"
						placeholder="Contains..."
						value={filters.code ?? ""}
						changeHandler={filterChanged}
					/>
					<TextBox
						id={"legacyType"}
						label="Legacy Type"
						placeholder="Contains..."
						value={filters.legacyType ?? ""}
						changeHandler={filterChanged}
					/>
					<FilterDropdown
						id={"comboStatus"}
						label="Quick Status"
						objectName="Statuses"
						values={COMBO_STATUS_OPTIONS}
						value={filters.comboStatus}
						changeHandler={filterChanged}
						clearable
						loaded
						fullLength
					/>
					<FilterDropdown
						id={STATUS}
						label="Detail Status"
						objectName="Statuses"
						values={statusesQuery.data}
						value={filters.status}
						changeHandler={filterChanged}
						clearable
						loaded={!statusesQuery.isLoading}
						fullLength
						isMulti
						valueProp="id"
						textProp="name"
					/>

					{/* Location Address */}
					<TextBox
						id={ADDRESS}
						label="Address"
						placeholder="Contains..."
						value={filters.address ?? ""}
						changeHandler={filterChanged}
					/>
					<TextBox
						id={CITY}
						label="City"
						placeholder="Contains..."
						value={filters.city ?? ""}
						changeHandler={filterChanged}
					/>
					<FilterDropdown
						id={STATE}
						label="State"
						objectName="States"
						values={statesQuery.data ?? []}
						value={filters.state}
						changeHandler={filterChanged}
						clearable
						loaded={!statesQuery.isLoading}
						fullLength
						isMulti
						valueProp="UsStateId"
						textProp="Name"
					/>
					<TextBox
						id={ZIP}
						label="Zip"
						placeholder="Contains..."
						value={filters.zip ?? ""}
						changeHandler={filterChanged}
					/>

					{/* Offices on Location */}
					<FilterDropdown
						id={CHANNEL_BRANCH}
						label={`${verbiage?.office} Channel`}
						objectName="Channels"
						values={branchChannelsQuery.data ?? []}
						value={filters.channel_branch}
						changeHandler={filterChanged}
						clearable
						loaded={!branchChannelsQuery.isLoading}
						fullLength
						valueProp="Id"
						textProp="Name"
						hidden
					/>
					<FilterDropdown
						id={MARKETER_BRANCH}
						label="Vendor"
						objectName="Vendors"
						values={branchMarketersQuery.data ?? []}
						value={filters.marketer_branch}
						changeHandler={filterChanged}
						clearable
						loaded={!branchMarketersQuery.isLoading}
						fullLength
						valueProp="Id"
						textProp="Name"
					/>
					<FilterDropdown
						id={STATE_BRANCH}
						label={`${verbiage?.office} State`}
						objectName="States"
						values={branchStatesQuery.data ?? []}
						value={filters.state_branch}
						changeHandler={filterChanged}
						clearable
						loaded={!branchStatesQuery.isLoading}
						fullLength
						valueProp="Id"
						textProp="Name"
					/>
					<FilterDropdown
						id={BRANCH}
						label={verbiage?.office}
						objectName={`${verbiage?.office}s`}
						values={branchesQuery.data ?? []}
						value={filters.branch}
						changeHandler={filterChanged}
						clearable
						loaded={!branchesQuery.isLoading}
						fullLength
						isMulti
						valueProp="BranchId"
						textProp="Name"
					/>
				</FiltersV2>

				<LocationsGrid filters={appliedFilters} setFilters={setAppliedFilters} />
			</main>
		</Loading>
	);
}
